import apiClient, { PaginatedResponse } from './client';

export type DomainStatus = 'pending' | 'active' | 'inactive' | 'error';
export type SSLStatus = 'pending' | 'active' | 'expired' | 'error';

export interface Domain {
  id: string;
  name: string;
  server_id: string;
  dns_provider: string | null;
  dns_record_type: string;
  dns_target: string | null;
  status: DomainStatus;
  ssl_enabled: boolean;
  ssl_auto_renew: boolean;
  created_at: string;
  updated_at: string;
  locations?: DomainLocation[];
}

export interface DomainCreate {
  name: string;
  server_id: string;
  dns_provider?: string;
  dns_record_type?: string;
  dns_target?: string;
  ssl_enabled?: boolean;
  ssl_auto_renew?: boolean;
}

export interface DomainUpdate {
  dns_provider?: string;
  dns_record_type?: string;
  dns_target?: string;
  ssl_enabled?: boolean;
  ssl_auto_renew?: boolean;
  status?: DomainStatus;
}

export interface SSLCertificate {
  id: string;
  domain_id: string;
  issuer: string | null;
  serial_number: string | null;
  status: SSLStatus;
  issued_at: string | null;
  expires_at: string | null;
  cert_path: string | null;
  key_path: string | null;
  created_at: string;
  updated_at: string;
}

export interface NginxConfig {
  id: string;
  name: string;
  content: string;
  domain_id: string | null;
  server_id: string;
  file_path: string | null;
  is_active: boolean;
  version: number;
  created_at: string;
  updated_at: string;
}

export interface NginxConfigCreate {
  name: string;
  content: string;
  server_id: string;
  domain_id?: string;
  file_path?: string;
}

export interface NginxConfigUpdate {
  content?: string;
  is_active?: boolean;
}

// Domain Location types
export interface DomainLocation {
  id: string;
  domain_id: string;
  app_id: string;
  path: string;
  upstream_port: number;
  upstream_host: string;
  priority: number;
  is_active: boolean;
  app_name?: string | null;
  created_at: string;
  updated_at: string;
}

export interface DomainLocationCreate {
  app_id: string;
  path?: string;
  upstream_port?: number;
  upstream_host?: string;
  priority?: number;
  is_active?: boolean;
}

export interface DomainLocationUpdate {
  path?: string;
  upstream_port?: number;
  upstream_host?: string;
  priority?: number;
  is_active?: boolean;
}

// Discovery types
export interface DiscoveredLocation {
  path: string;
  upstream_host: string;
  upstream_port: number;
  matched_app_id?: string | null;
  matched_app_name?: string | null;
}

export interface DiscoveredDomain {
  name: string;
  source: 'docker' | 'host';
  config_file: string;
  ssl_enabled: boolean;
  ssl_cert_path?: string | null;
  ssl_key_path?: string | null;
  locations: DiscoveredLocation[];
  existing_domain_id?: string | null;
}

export interface DiscoveryRequest {
  server_id: string;
  source?: 'docker' | 'host' | 'both';
  auto_import?: boolean;
}

export interface DiscoveryResult {
  discovered: DiscoveredDomain[];
  new_count: number;
  updated_count: number;
  skipped_count: number;
  errors?: string[];
}

export interface ImportDomainsRequest {
  server_id: string;
  domains: string[];
  source?: 'docker' | 'host' | 'both';
}

export interface ImportResult {
  imported_count: number;
  domain_ids: string[];
  errors?: string[];
}

export interface ListDomainsParams {
  page?: number;
  limit?: number;
  search?: string;
  server_id?: string;
  status?: DomainStatus;
}

export const domainsApi = {
  list: async (params: ListDomainsParams = {}): Promise<PaginatedResponse<Domain>> => {
    const response = await apiClient.get<PaginatedResponse<Domain>>('/api/v1/domains', { params });
    return response.data;
  },

  get: async (id: string): Promise<Domain> => {
    const response = await apiClient.get<Domain>(`/api/v1/domains/${id}`);
    return response.data;
  },

  create: async (data: DomainCreate): Promise<Domain> => {
    const response = await apiClient.post<Domain>('/api/v1/domains', data);
    return response.data;
  },

  update: async (id: string, data: DomainUpdate): Promise<Domain> => {
    const response = await apiClient.patch<Domain>(`/api/v1/domains/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/v1/domains/${id}`);
  },

  // SSL
  getSSL: async (domainId: string): Promise<SSLCertificate> => {
    const response = await apiClient.get<SSLCertificate>(`/api/v1/domains/${domainId}/ssl`);
    return response.data;
  },

  issueSSL: async (domainId: string): Promise<SSLCertificate> => {
    const response = await apiClient.post<SSLCertificate>(`/api/v1/domains/${domainId}/ssl/issue`);
    return response.data;
  },

  // Discovery
  discover: async (data: DiscoveryRequest): Promise<DiscoveryResult> => {
    const response = await apiClient.post<DiscoveryResult>('/api/v1/domains/discover', data);
    return response.data;
  },

  importDomains: async (data: ImportDomainsRequest): Promise<ImportResult> => {
    const response = await apiClient.post<ImportResult>('/api/v1/domains/import', data);
    return response.data;
  },

  // Locations
  listLocations: async (domainId: string): Promise<DomainLocation[]> => {
    const response = await apiClient.get<DomainLocation[]>(`/api/v1/domains/${domainId}/locations`);
    return response.data;
  },

  addLocation: async (domainId: string, data: DomainLocationCreate): Promise<DomainLocation> => {
    const response = await apiClient.post<DomainLocation>(`/api/v1/domains/${domainId}/locations`, data);
    return response.data;
  },

  updateLocation: async (domainId: string, locationId: string, data: DomainLocationUpdate): Promise<DomainLocation> => {
    const response = await apiClient.patch<DomainLocation>(`/api/v1/domains/${domainId}/locations/${locationId}`, data);
    return response.data;
  },

  removeLocation: async (domainId: string, locationId: string): Promise<void> => {
    await apiClient.delete(`/api/v1/domains/${domainId}/locations/${locationId}`);
  },

  // Deploy nginx config for domain
  deployNginx: async (domainId: string): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>(`/api/v1/domains/${domainId}/deploy`);
    return response.data;
  },
};

export const nginxApi = {
  list: async (params: { page?: number; limit?: number; server_id?: string } = {}): Promise<PaginatedResponse<NginxConfig>> => {
    const response = await apiClient.get<PaginatedResponse<NginxConfig>>('/api/v1/nginx', { params });
    return response.data;
  },

  get: async (id: string): Promise<NginxConfig> => {
    const response = await apiClient.get<NginxConfig>(`/api/v1/nginx/${id}`);
    return response.data;
  },

  create: async (data: NginxConfigCreate): Promise<NginxConfig> => {
    const response = await apiClient.post<NginxConfig>('/api/v1/nginx', data);
    return response.data;
  },

  update: async (id: string, data: NginxConfigUpdate): Promise<NginxConfig> => {
    const response = await apiClient.patch<NginxConfig>(`/api/v1/nginx/${id}`, data);
    return response.data;
  },

  deploy: async (id: string): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>(`/api/v1/nginx/${id}/deploy`);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/v1/nginx/${id}`);
  },
};
